/**
 * MigrateProviderDialog - Modal para migrar provider de uma instância ativa.
 *
 * Permite ao usuário selecionar uma instância "released" (provisionada no
 * upgrade) para substituir o provider da instância "in_use" selecionada.
 * A instância released será consumida (cancelled) após a migração.
 *
 * @version 2.0.0
 * RELEASE: 2026-06-29 v2.0.0 - Padronização visual premium replicando o
 *                              layout do AddInstanceDialog (header com
 *                              gradient + ícone badge, cards de provider em
 *                              paleta sutil, footer `bg-muted/10`). Sem
 *                              alterações em lógica/queries/mutations.
 * RELEASE: 2026-03-05 v1.0.0 - Versão inicial.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  ArrowRightLeft,
  AlertTriangle,
  CheckCircle2,
  MessageCircle,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import type { Instance } from '@/hooks/useInstances';
import { cn } from '@/lib/utils';

const logger = createLogger('MigrateProviderDialog');

interface MigrateProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Instância ativa (in_use) que terá o provider migrado */
  sourceInstance: Instance | null;
  /** Lista de instâncias released disponíveis para migração */
  releasedInstances: Instance[];
  tenantId: string;
  onSuccess?: () => void;
}

export function MigrateProviderDialog({
  open,
  onOpenChange,
  sourceInstance,
  releasedInstances,
  tenantId,
  onSuccess,
}: MigrateProviderDialogProps) {
  const queryClient = useQueryClient();
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [isMigrating, setIsMigrating] = useState(false);

  // Filtra instâncias released com provider diferente do source
  const eligibleTargets = releasedInstances.filter(
    (inst) => inst.provider_id !== sourceInstance?.provider_id,
  );

  const selectedTarget = eligibleTargets.find((i) => i.id === selectedTargetId);

  /** Executa a migração via RPC migrate_instance_provider */
  const handleMigrate = async () => {
    if (!sourceInstance || !selectedTargetId) return;

    setIsMigrating(true);
    logger.info('Iniciando migração de provider', {
      sourceId: sourceInstance.id,
      targetId: selectedTargetId,
    });

    try {
      const { data, error } = await (supabase.rpc as any)(
        'migrate_instance_provider',
        {
          p_source_instance_id: sourceInstance.id,
          p_target_instance_id: selectedTargetId,
          p_tenant_hint: tenantId,
        },
      );

      if (error) {
        logger.error('Erro na migração de provider', { error });
        toast.error(error.message || 'Erro ao migrar provider');
        return;
      }

      logger.info('Migração concluída com sucesso', { result: data });
      toast.success(
        `Provider migrado de ${sourceInstance.provider_display_name} para ${selectedTarget?.provider_display_name}`,
      );

      queryClient.invalidateQueries({ queryKey: ['instances', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['inboxes', tenantId] });

      setSelectedTargetId('');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      logger.error('Erro não tratado na migração', { err });
      toast.error('Erro inesperado ao migrar provider');
    } finally {
      setIsMigrating(false);
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) setSelectedTargetId('');
    onOpenChange(value);
  };

  if (!sourceInstance) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-2xl p-0 gap-0 overflow-hidden flex flex-col"
        hideCloseButton={false}
      >
        {/* ===== HEADER PREMIUM ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <ArrowRightLeft className="h-3.5 w-3.5 text-primary" />
              </div>
              Migrar provider
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Selecione uma instância disponível para substituir o provider
              atual. As configurações do provider anterior serão removidas.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO ===== */}
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Card da instância atual */}
          <div className="rounded-lg border p-3 flex items-center gap-3 bg-muted/20">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
              <MessageCircle className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Instância atual
              </p>
              <p className="text-sm font-semibold text-foreground truncate">
                {sourceInstance.instance_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {sourceInstance.provider_display_name}
                {sourceInstance.inbox_name && (
                  <> · {sourceInstance.inbox_name}</>
                )}
              </p>
            </div>
          </div>

          {/* Vazio: nenhum target elegível */}
          {eligibleTargets.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
              <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="text-sm font-medium text-foreground">
                Sem instâncias disponíveis
              </p>
              <p className="text-xs text-muted-foreground">
                Não há instâncias com provider diferente disponíveis para
                migração. Novas instâncias são provisionadas automaticamente ao
                fazer upgrade de plano.
              </p>
            </div>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-1 pt-1">
                Selecione o novo provider
              </p>
              {eligibleTargets.map((target) => {
                const isSelected = selectedTargetId === target.id;
                return (
                  <button
                    type="button"
                    key={target.id}
                    onClick={() => setSelectedTargetId(target.id)}
                    className={cn(
                      'w-full text-left rounded-lg border p-4 transition-all flex items-center gap-3',
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : 'border-border hover:bg-muted/30',
                    )}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                      <MessageCircle className="h-5 w-5 text-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {target.provider_display_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {target.instance_name}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className="text-[10px] font-normal text-muted-foreground border-border"
                    >
                      Disponível
                    </Badge>
                    {isSelected && (
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                    )}
                  </button>
                );
              })}
            </>
          )}

          {/* Aviso de confirmação */}
          {selectedTarget && (
            <div className="rounded-lg border border-dashed p-4 flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 text-foreground mt-0.5 shrink-0" />
              <p className="text-xs text-foreground leading-relaxed">
                Ao confirmar, o provider{' '}
                <strong>{sourceInstance.provider_display_name}</strong> será
                substituído por{' '}
                <strong>{selectedTarget.provider_display_name}</strong>.
                {sourceInstance.inbox_name && (
                  <>
                    {' '}A inbox <strong>{sourceInstance.inbox_name}</strong>{' '}
                    precisará ser reconfigurada com as credenciais do novo
                    provider.
                  </>
                )}
              </p>
            </div>
          )}
        </div>

        {/* ===== FOOTER PREMIUM ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={isMigrating}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleMigrate}
            disabled={!selectedTargetId || isMigrating}
          >
            {isMigrating ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <ArrowRightLeft className="h-4 w-4 mr-1.5" />
            )}
            Confirmar migração
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
